/**
 * RatingScalesSummary.js
 *
 * The Rating Scales card on Provider Settings. One block per pathway the
 * organisation offers, in the same shape as Adult ADHD and Adult Autism:
 * a readable pathway name, then the scales that new cases receive.
 *
 * CYP ADHD is included on purpose. The child does not complete a rating
 * scale, so there is no "patient rating scales" line. SNAP-IV 26 is listed
 * as the informant rating scale because every organisation assigns it to
 * the parent or carer and the school representative.
 */

import React from 'react';
import { buildInstrumentSummaries } from '../config/instrumentSettingsConfig';

function CountLine({ count, noun, labels }) {
  const plural = count === 1 ? noun : `${noun}s`;
  const heading = count > 0
    ? `${count} ${plural} enabled`
    : `No ${plural} enabled`;

  return (
    <div className="flex items-start gap-2 text-sm text-gray-700">
      <span className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
        ✓
      </span>
      <div>
        <span className="font-medium">{heading}</span>
        {labels.length > 0 && (
          <p className="text-xs text-gray-500 mt-0.5">
            {labels.join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}

export default function RatingScalesSummary({
  isLoading = false,
  instruments = null,
  orgName = '',
  availableModules = ['adultAdhd'],
  onManage,
}) {
  const summaries = instruments
    ? buildInstrumentSummaries({ instruments, availableModules, orgName })
    : [];

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
      <h3 className="text-lg font-semibold mb-1">Rating Scales</h3>
      <p className="text-sm text-gray-500 mb-4">
        Manage which standardised rating scales are assigned to newly-created patients and informants.
      </p>

      {isLoading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-6 bg-gray-200 rounded-lg w-3/4" />
          ))}
        </div>
      ) : instruments ? (
        <div className="space-y-4">
          {summaries.map((summary) => (
            <div
              key={summary.moduleKey}
              className="rounded-lg border border-gray-200 bg-white p-4 space-y-2"
            >
              <h4 className="text-sm font-semibold text-gray-900">
                {summary.moduleLabel}
              </h4>

              {summary.showPatientScales && (
                <CountLine
                  count={summary.enabledPatientScales.length}
                  noun="patient rating scale"
                  labels={summary.enabledPatientScales.map((scale) => scale.label)}
                />
              )}

              {summary.hasInformantConfig && (
                <CountLine
                  count={summary.enabledInformantLabels.length}
                  noun={summary.informantNoun}
                  labels={summary.enabledInformantLabels}
                />
              )}
            </div>
          ))}

          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onManage}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
            >
              Manage Rating Scales
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">
          Rating scale configuration is not available for your organisation.
        </p>
      )}
    </div>
  );
}
